import React, { useState } from 'react';
import { Check, X, Clock, ChevronRight, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { MoldOrder, MoldOrderStatus, MoldOrderShownInTimelineAsStatus } from '../../types/mold';
import { isFinalStep } from '../MoldDetail/utils';
import { useAppState } from '../../context/AppStateContext';
import HorizontalStepDetailCard from './HorizontalStepDetailCard';
import HorizontalCompleteStep from './HorizontalCompleteStep';

interface ProgressStep {
  id: MoldOrderStatus;
  name: string;
  description: string;
  icon: React.FC<any>;
}

interface HorizontalStepperProps {
  moldOrder: MoldOrder;
  selectedStepIndex: number;
  onStepClick: (stepIndex: number) => void;
}

const HorizontalStepper: React.FC<HorizontalStepperProps> = ({
  moldOrder,
  selectedStepIndex,
  onStepClick,
}) => {
  const {
    getStatusText,
    getStatusIcon,
  } = useAppState();
  const [showCompleted, setShowCompleted] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 640; // show completed by default on desktop, hide on mobile
  });
  const [completedExpanded, setCompletedExpanded] = useState<boolean>(false);
  // Number of completed steps to show when collapsed (change this to adjust preview size)
  const COMPLETED_PREVIEW_COUNT = 2;

  // Get state instance for a specific step
  const getStepStateInstance = (stepIndex: number) => {
    if (!moldOrder.stateInstances || !moldOrder.workflowStructure) return null;
    const step = moldOrder.workflowStructure[stepIndex];
    if (!step) return null;

    return moldOrder.stateInstances[stepIndex] || null;
  };

  const mapWorkflowStructureWithUI = (): ProgressStep[] => {
    if (!moldOrder.workflowStructure) return [];

    return moldOrder.workflowStructure.map((step) => {
      return {
        id: step.statusName,
        name: getStatusText(step.statusName as MoldOrderStatus, moldOrder.type, moldOrder.orderWorkflowDefinition),
        icon: getStatusIcon(step.statusName as MoldOrderStatus, moldOrder.type, moldOrder.orderWorkflowDefinition) || Check,
        description: '',
      } as ProgressStep
    });
  };

  const getStepStyle = (
    currentStatus: MoldOrderStatus,
    stepStatus: MoldOrderStatus,
    isPastStep: boolean,
    isCurrentStep: boolean,
    isSelected: boolean,
  ): string => {
    const isCompletedFinalStep =
      stepStatus === MoldOrderStatus.COMPLETED && isFinalStep(currentStatus);

    if (isSelected) {
      return 'bg-blue-600 border-blue-600 text-white ring-2 ring-blue-300';
    }

    if (isCurrentStep || isCompletedFinalStep) {
      return isFinalStep(currentStatus)
        ? currentStatus === MoldOrderStatus.DONE
          ? 'bg-green-500 border-green-500 text-white'
          : 'bg-red-500 border-red-500 text-white'
        : 'bg-blue-500 border-blue-500 text-white';
    }

    return isPastStep
      ? 'bg-green-400 border-green-400 text-white'
      : 'bg-gray-300 border-gray-300 text-gray-500';
  };

  const getStepName = (step: ProgressStep) => {
    return getStatusText(step.id, moldOrder.type, moldOrder.orderWorkflowDefinition);
  };

  const getReworkCount = (stepIndex: number): number => {
    if (!moldOrder.stateInstances) return 0;
    const step = moldOrder.workflowStructure?.[stepIndex];
    if (!step) return 0;

    // Count how many times this status appears in state instances
    return moldOrder.stateInstances.filter(
      instance => instance.status === step.statusName
    ).length;
  };

  const getStepIcon = (step: ProgressStep, isPastStep: boolean, isCurrentStep: boolean, reworkCount: number) => {
    if (isFinalStep(step.id) && moldOrder.status === MoldOrderStatus.END_REJECTED) {
      return <X className="w-5 h-5 sm:w-4 sm:h-4" />;
    }

    if (isPastStep) {
      return <Check className="w-5 h-5 sm:w-4 sm:h-4" />;
    }

    if (isCurrentStep && reworkCount > 1) {
      return <Clock className="w-5 h-5 sm:w-4 sm:h-4" />;
    }

    return <step.icon className="w-5 h-5 sm:w-4 sm:h-4" />;
  };

  const steps = mapWorkflowStructureWithUI();
  // Grouping helpers
  const allIndexes = steps.map((_, idx) => idx);
  const completedIndexes = allIndexes.filter(i => moldOrder.workflowStructure?.[i]?.shownInTimelineAs === MoldOrderShownInTimelineAsStatus.PAST);
  const currentIndex = allIndexes.find(i => moldOrder.workflowStructure?.[i]?.shownInTimelineAs === MoldOrderShownInTimelineAsStatus.CURRENT) ?? -1;
  const futureIndexes = allIndexes.filter(i => {
    const s = moldOrder.workflowStructure?.[i]?.shownInTimelineAs;
    return s !== MoldOrderShownInTimelineAsStatus.PAST && s !== MoldOrderShownInTimelineAsStatus.CURRENT;
  });

  const getCollapsedCompleted = () => {
    const lastN = COMPLETED_PREVIEW_COUNT;
    const start = Math.max(0, completedIndexes.length - lastN);
    return completedIndexes.slice(start);
  };

  const buildVisibleIndexes = () => {
    const visible: number[] = [];
    if (showCompleted) {
      visible.push(...(completedExpanded ? completedIndexes : getCollapsedCompleted()));
    }
    if (currentIndex >= 0) visible.push(currentIndex);
    visible.push(...futureIndexes);
    return visible;
  };

  const visibleStepIndexes = buildVisibleIndexes();

  return (
    <div className="w-full">
      <div className="px-4 py-2 flex flex-col sm:flex-row items-start sm:items-center justify-between">
        <div className="text-xs text-gray-500 w-full sm:w-auto text-center sm:text-left">แสดง {visibleStepIndexes.length}/{steps.length} ขั้นตอน</div>
        <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2 w-full sm:w-auto my-2 sm:my-0">
          <button
            type="button"
            aria-pressed={showCompleted}
            onClick={() => setShowCompleted(prev => !prev)}
            className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-medium shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1
              bg-white text-blue-600 border border-blue-200 hover:bg-blue-50 focus:ring-blue-300 w-full sm:w-auto`}
          >
            {showCompleted ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            <span className="whitespace-nowrap text-center">{showCompleted ? 'ซ่อนขั้นตอนที่เสร็จแล้ว' : 'แสดงขั้นตอนที่เสร็จแล้ว'}</span>
          </button>

          {showCompleted && completedIndexes.length > COMPLETED_PREVIEW_COUNT && (
            <button
              type="button"
              onClick={() => setCompletedExpanded(prev => !prev)}
              className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-full text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
            >
              {completedExpanded ? 'ซ่อนทั้งหมด' : `ดูทั้งหมด (${completedIndexes.length})`}
            </button>
          )}
        </div>
      </div>
      {/* Scroll indicators */}
      <div className="relative">
        <div className="flex flex-col sm:flex-row sm:items-start items-center gap-4 sm:gap-6 overflow-x-auto overflow-y-visible pb-4 px-4">
          <HorizontalCompleteStep
            showCompleted={showCompleted}
            completedExpanded={completedExpanded}
            completedIndexes={completedIndexes}
            previewCount={COMPLETED_PREVIEW_COUNT}
            onExpand={() => setCompletedExpanded(true)}
          />

          {visibleStepIndexes.map((index) => {
            const workflowStep = moldOrder.workflowStructure?.[index];
            const isCurrentStep = workflowStep?.shownInTimelineAs === MoldOrderShownInTimelineAsStatus.CURRENT;
            const isPastStep = workflowStep?.shownInTimelineAs === MoldOrderShownInTimelineAsStatus.PAST;
            const isSelected = selectedStepIndex === index;
            const reworkCount = getReworkCount(index);
            const stateInstance = getStepStateInstance(index);

            const step = steps[index];
            const stepClassName = getStepStyle(
              moldOrder.status,
              step.id,
              isPastStep,
              isCurrentStep,
              isSelected,
            );

            return (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center w-full sm:flex-shrink-0 sm:min-w-[60px] xl:min-w-[80px] sm:max-w-[120px] lg:max-w-[160px] py-0 sm:py-4">
                  {/* Step button and label container */}
                  <div className="flex flex-col items-center mb-3">
                    <div className="relative flex items-center justify-center mb-2">
                      <button
                        data-step-index={index}
                        onClick={() => onStepClick(index)}
                        className={`relative flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300
                          w-12 h-12 sm:w-10 sm:h-10
                          ${stepClassName}
                          rounded-full
                          hover:scale-105
                        `}
                        aria-label={`ขั้นตอน ${index + 1}: ${getStepName(step)}`}
                      >
                        {getStepIcon(step, isPastStep, isCurrentStep, reworkCount)}
                        {/* Current step pulse animation */}
                        {isCurrentStep && (
                          <div className="absolute inset-0 rounded-full bg-blue-500 opacity-20 animate-ping"></div>
                        )}
                      </button>
                    </div>
                    <div className="text-center w-full">
                      <p className={`text-sm sm:text-xs font-medium leading-tight ${isCurrentStep || isSelected ? 'text-black' : 'text-gray-500'
                        }`} style={{ lineHeight: '1.2rem' }}>
                        {getStepName(step)}
                      </p>
                    </div>
                  </div>

                  {/* Detail card container with consistent alignment */}
                  <div className="w-full flex justify-center">
                    {stateInstance && (isPastStep || isCurrentStep) && !isFinalStep(step.id) && (
                      <HorizontalStepDetailCard
                        assignedTo={stateInstance.assignedTo?.fullname ?? stateInstance.assignedTo?.username ?? ''}
                        timestamp={stateInstance.startTime ?? ''}
                        endTime={stateInstance.endTime ?? null}
                        assigneeData={stateInstance.assignedTo}
                        stateInstance={stateInstance}
                        isCurrentStep={isCurrentStep}
                        onClick={() => onStepClick(index)}
                      />
                    )}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden sm:flex items-start justify-center pt-6 flex-shrink-0">
                    <ChevronRight className="text-gray-400" size={18} />
                  </div>
                )}
                {index < steps.length - 1 && (
                  <div className="flex sm:hidden items-center justify-center w-full py-0 sm:py-2">
                    <ChevronDown className="text-gray-400" size={18} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step counter and navigation hints */}
      <div className="flex items-center justify-between text-xs text-gray-500 mt-2 px-4">
        <span className="hidden sm:inline">เลื่อนเพื่อดูขั้นตอนเพิ่มเติม</span>
        <span className="sm:hidden">เลื่อนลงเพื่อดูขั้นตอนเพิ่มเติม</span>
        <span>{steps.length} ขั้นตอน</span>
      </div>
    </div>
  );
};

export default HorizontalStepper;
